import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import ChangePassword from "models/users/password-change";
import {
  CompanyWorkgroup,
  UserProfile,
  UserWithWorkgroups,
} from "models/users/user-workgroups";
import { Observable } from "rxjs";
import OrganizationChange from "../../models/users/organization-change";
import SelectedOrganization from "../../models/users/selected-organization";
import { ConfigurationService } from "../configuration/configuration.service";

enum AccessLevel {
  VIEW = 1,
  ADD = 2,
  UPDATE = 4,
  DELETE = 8,
}

interface DomainAccess {
  domain: string;
  access: number;
}

@Injectable()
class UsersService {
  private readonly usersServiceUrl: string;
  private readonly accessServiceUrl: string;
  private readonly apiUrl: string;
  private readonly authService: AuthService;

  constructor(
    private http: HttpClient,
    private config: ConfigurationService,
    private auth: AuthService
  ) {
    this.apiUrl = config.getConfig().apiUrl;
    this.usersServiceUrl = config.getConfig().usersUrl;
    this.accessServiceUrl = config.getConfig().accessUrl;
    this.authService = auth;
  }
  /**
   * users-service-v2 uses the apiUrl
   */

  public changePassword(
    userId: string,
    password: ChangePassword
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/users/${userId}/change-password`,
      password
    );
  }

  public async doesUserExist(userId: string): Promise<any> {
    const responseBody: any = await this.http
      .get(`${this.apiUrl}/users/user-exists/${userId}`)
      .toPromise();

    try {
      const parsedResponse: any = JSON.parse(responseBody);
      console.log({ parsedResponse });

      return parsedResponse.exists === "true";
    } catch {
      return responseBody.exists === true;
    }
  }

  public getUser(userId: string): Observable<UserWithWorkgroups> {
    return this.http
      .get<UserWithWorkgroups>(`${this.apiUrl}/users/${userId}`)
      .map((x) => {
        let y: UserWithWorkgroups = {
          active: x.active,
          createdDate: x.createdDate,
          tenantId: x.tenantId,
          updatedDate: x.updatedDate,
          userId: x.userId,
          userName: x.userName,
          workgroups: [],
        };

        for (let i = 0; i < x.workgroups.length; i++) {
          y.workgroups.push({
            createdDate: x.workgroups[i].createdDate,
            id: x.workgroups[i].id,
            updatedDate: x.workgroups[i].updatedDate,
            tenantId: x.workgroups[i].tenantId,
          });
        }

        return y;
      });
  }

  public getUserRoles(userId: string): Observable<CompanyWorkgroup[]> {
    return this.http.get<CompanyWorkgroup[]>(`${this.apiUrl}/${userId}/roles`);
  }

  public getUserProfile(userId: string): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/${userId}`);
  }

  /**
   * Changes the user's currently selected organization
   */
  public changeOrganization(organizationChange: OrganizationChange) {
    return this.http.post(
      `${this.usersServiceUrl}/updateChangeOrg`,
      organizationChange
    );
  }

  /**
   * Returns the user's selected organization
   */
  // master branch(replace)
  public getSelectedOrganization(
    userId: number
  ): Observable<SelectedOrganization> {
    return this.http
      .get<SelectedOrganization>(
        `${this.usersServiceUrl}/user?userId=${userId}`
      )
      .map((x: any) => {
        return {
          userId: x.UserId,
          organizationId: x.OrganizationId,
          organizationName: x.OrganizationName,
          userName: x.UserName,
          updatedDate: x.UpdatedDate,
        };
      });
  }

  /**
   * Returns true if the user has the access level requested for this domain
   * @param userId the user to check
   * @param organizationId the organization id
   * @param domain the domain/resource requested
   */
  public getAccess(
    userId: number,
    organizationId: number,
    domain: string
  ): Observable<DomainAccess> {
    return this.http.get<DomainAccess>(
      `${this.accessServiceUrl}/users/${userId}/has-access?organizationId=${organizationId}&domain=${domain}`
    );
  }

  /**
   * Returns true if the access level matches the supplied DomainAccess
   * @param access the domain access to check
   * @param accessLevel the level to compare for
   * @param featureFlag the flag at the feature level
   */
  public hasAccess(
    access: DomainAccess,
    accessLevel: AccessLevel,
    featureFlag: boolean = true
  ): boolean {
    return (access.access & accessLevel) === accessLevel && featureFlag;
  }
}

export { UsersService, AccessLevel, DomainAccess };



// WEBPACK FOOTER //
// ./src/providers/users-service/users.service.ts